import { openDB, DBSchema, IDBPDatabase } from 'idb';

export type Confidence = 'lost' | 'sorta' | 'got_it';
export type InputType = 'snap' | 'type' | 'browse';

export interface ProblemRecord {
  id: string;
  timestamp: number;
  chapter: string | null;
  topic: string;
  input_type: InputType;
  hints_used: number;
  confidence: Confidence | null;
  duration_seconds: number;
}

export interface SessionRecord {
  date: string;
  minutes: number;
  problems: number;
}

interface MathBuddyDB extends DBSchema {
  problems: {
    key: string;
    value: ProblemRecord;
    indexes: { 'by-timestamp': number; 'by-chapter': string };
  };
  sessions: {
    key: string;
    value: SessionRecord;
  };
  settings: {
    key: string;
    value: any;
  };
}

let dbPromise: Promise<IDBPDatabase<MathBuddyDB>> | null = null;

export const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB<MathBuddyDB>('math-buddy', 1, {
      upgrade(db) {
        const problems = db.createObjectStore('problems', { keyPath: 'id' });
        problems.createIndex('by-timestamp', 'timestamp');
        problems.createIndex('by-chapter', 'chapter');
        db.createObjectStore('sessions', { keyPath: 'date' });
        db.createObjectStore('settings');
      },
    });
  }
  return dbPromise;
};

export async function logProblem(problem: ProblemRecord) {
  const db = await getDB();
  await db.put('problems', problem);
}

export async function updateConfidence(id: string, confidence: Confidence) {
  const db = await getDB();
  const existing = await db.get('problems', id);
  if (existing) {
    await db.put('problems', { ...existing, confidence });
  }
}

export async function updateProblem(id: string, patch: Partial<ProblemRecord>) {
  const db = await getDB();
  const existing = await db.get('problems', id);
  if (existing) {
    await db.put('problems', { ...existing, ...patch });
  }
}

export async function allProblems(): Promise<ProblemRecord[]> {
  const db = await getDB();
  const tx = db.transaction('problems', 'readonly');
  const index = tx.store.index('by-timestamp');
  const all = await index.getAll();
  return all.sort((a, b) => b.timestamp - a.timestamp);
}

export async function bumpSession(minutes: number) {
  const db = await getDB();
  const date = new Date().toISOString().slice(0, 10);
  const existing = (await db.get('sessions', date)) ?? {
    date,
    minutes: 0,
    problems: 0,
  };
  await db.put('sessions', {
    ...existing,
    minutes: existing.minutes + minutes,
    problems: existing.problems + 1,
  });
}

export async function allSessions(): Promise<SessionRecord[]> {
  const db = await getDB();
  const all = await db.getAll('sessions');
  return all.sort((a, b) => a.date.localeCompare(b.date));
}

export async function getSetting<T>(key: string, fallback: T): Promise<T> {
  const db = await getDB();
  const value = await db.get('settings', key);
  return (value as T) ?? fallback;
}

export async function setSetting(key: string, value: unknown) {
  const db = await getDB();
  await db.put('settings', value, key);
}
